import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isSameMonth,
  isToday,
  isWeekend,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { de } from 'date-fns/locale';
import PageHeader from '../components/common/PageHeader';
import TableSkeleton from '../components/common/TableSkeleton';
import { anwesenheitService } from '../services/anwesenheitService';
import { AnwesenheitEintrag, AnwesenheitMitarbeiter } from '../types';

function overlapsDay(eintrag: AnwesenheitEintrag, day: Date): boolean {
  const key = format(day, 'yyyy-MM-dd');
  return eintrag.von <= key && eintrag.bis >= key;
}

const AnwesenheitPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mitarbeiter, setMitarbeiter] = useState<AnwesenheitMitarbeiter[]>([]);
  const [eintraege, setEintraege] = useState<AnwesenheitEintrag[]>([]);

  const von = format(startOfMonth(month), 'yyyy-MM-dd');
  const bis = format(endOfMonth(month), 'yyyy-MM-dd');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await anwesenheitService.kalender(von, bis);
      setMitarbeiter(data.mitarbeiter ?? []);
      setEintraege(data.eintraege);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [von, bis]);

  useEffect(() => {
    load();
  }, [load]);

  const days = useMemo(
    () => eachDayOfInterval({ start: startOfMonth(month), end: endOfMonth(month) }),
    [month]
  );

  const mitarbeiterRows = useMemo(() => {
    const itemsByMa = new Map<number, AnwesenheitEintrag[]>();
    for (const e of eintraege) {
      const list = itemsByMa.get(e.mitarbeiter_id) ?? [];
      list.push(e);
      itemsByMa.set(e.mitarbeiter_id, list);
    }
    return mitarbeiter.map((m) => ({
      id: m.id,
      name: m.name,
      items: itemsByMa.get(m.id) ?? [],
    }));
  }, [mitarbeiter, eintraege]);

  const cellBg = (items: AnwesenheitEintrag[]): string | undefined => {
    const hasWorkation = items.some((i) => i.kategorie === 'workation');
    const hasFehlzeit = items.some((i) => i.kategorie === 'fehlzeit');
    if (hasWorkation && hasFehlzeit) return isDark ? '#5c4d1f' : '#fff3e0';
    if (hasWorkation) return isDark ? '#1a3a5c' : '#e3f2fd';
    if (hasFehlzeit) return isDark ? '#3a3a3a' : '#eceff1';
    return undefined;
  };

  const cellLabel = (items: AnwesenheitEintrag[]): string => {
    const hasWorkation = items.some((i) => i.kategorie === 'workation');
    const hasFehlzeit = items.some((i) => i.kategorie === 'fehlzeit');
    if (hasWorkation && hasFehlzeit) return 'W/F';
    if (hasWorkation) return 'W';
    if (hasFehlzeit) return 'F';
    return '';
  };

  return (
    <Box>
      <PageHeader
        title="Anwesenheitskalender"
        subtitle="Fehlzeiten undifferenziert · Workation für alle sichtbar"
        breadcrumbs={[{ label: 'Anwesenheit' }]}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => setMonth((m) => subMonths(m, 1))} size="small">
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h6" sx={{ minWidth: 180, textAlign: 'center', fontWeight: 700 }}>
          {format(month, 'MMMM yyyy', { locale: de })}
        </Typography>
        <IconButton onClick={() => setMonth((m) => addMonths(m, 1))} size="small">
          <ChevronRightIcon />
        </IconButton>
        <Tooltip title="Aktueller Monat">
          <IconButton onClick={() => setMonth(startOfMonth(new Date()))} size="small">
            <TodayIcon />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Chip size="small" label="F = Fehlzeit" sx={{ bgcolor: isDark ? '#3a3a3a' : '#eceff1' }} />
        <Chip size="small" label="W = Workation" sx={{ bgcolor: isDark ? '#1a3a5c' : '#e3f2fd' }} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <TableSkeleton columns={10} rows={8} />
      ) : (
        <Paper
          sx={{
            overflow: 'auto',
            borderRadius: 2,
            border: `1px solid ${isDark ? '#2c3358' : '#e0e4ea'}`,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `180px repeat(${days.length}, minmax(28px, 1fr))`,
              minWidth: 180 + days.length * 28,
            }}
          >
            <Box
              sx={{
                position: 'sticky',
                left: 0,
                zIndex: 2,
                bgcolor: isDark ? '#1a1f36' : '#f8f9fb',
                p: 1,
                borderBottom: `1px solid ${isDark ? '#2c3358' : '#e0e4ea'}`,
                fontWeight: 700,
                fontSize: '0.75rem',
              }}
            >
              Mitarbeiter
            </Box>
            {days.map((d) => (
              <Box
                key={d.toISOString()}
                sx={{
                  p: 0.5,
                  textAlign: 'center',
                  borderBottom: `1px solid ${isDark ? '#2c3358' : '#e0e4ea'}`,
                  borderLeft: `1px solid ${isDark ? '#2c3358' : '#eef1f5'}`,
                  fontSize: '0.65rem',
                  fontWeight: isToday(d) ? 700 : 500,
                  color: isWeekend(d) ? 'text.disabled' : 'text.secondary',
                  bgcolor: isToday(d) ? (isDark ? '#243050' : '#e8f5e9') : undefined,
                }}
              >
                {format(d, 'd')}
                <br />
                {format(d, 'EEEEE', { locale: de })}
              </Box>
            ))}

            {mitarbeiterRows.length === 0 && (
              <Box sx={{ gridColumn: '1 / -1', p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">Keine aktiven Mitarbeiter vorhanden</Typography>
              </Box>
            )}

            {mitarbeiterRows.map((row) => (
              <React.Fragment key={row.id}>
                <Box
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    bgcolor: isDark ? '#151a2e' : '#fff',
                    p: 1,
                    borderBottom: `1px solid ${isDark ? '#2c3358' : '#eef1f5'}`,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {row.name}
                </Box>
                {days.map((d) => {
                  const dayItems = row.items.filter((i) => overlapsDay(i, d));
                  const bg = cellBg(dayItems);
                  const label = cellLabel(dayItems);
                  return (
                    <Tooltip
                      key={`${row.id}-${d.toISOString()}`}
                      title={
                        dayItems.length
                          ? dayItems
                              .map((i) =>
                                i.kategorie === 'workation' ? 'Workation' : 'Fehlzeit'
                              )
                              .join(', ')
                          : ''
                      }
                    >
                      <Box
                        sx={{
                          minHeight: 32,
                          borderBottom: `1px solid ${isDark ? '#2c3358' : '#eef1f5'}`,
                          borderLeft: `1px solid ${isDark ? '#2c3358' : '#eef1f5'}`,
                          bgcolor: bg || (isWeekend(d) && isSameMonth(d, month) ? (isDark ? '#12162a' : '#fafbfc') : undefined),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          color: 'text.secondary',
                        }}
                      >
                        {label}
                      </Box>
                    </Tooltip>
                  );
                })}
              </React.Fragment>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default AnwesenheitPage;
